/**
 * Vengeance UI — Framer Motion Animation Variants
 * Inspired by Vengeance UI (MIT) — vengenceui.com / GitHub Ashutoshx7/VengeanceUI
 * Flux Panel integration layer.
 *
 * Usage:
 *   import { fadeInUp, springHover, modalVariants, staggerContainer } from './animations';
 *   <motion.div variants={fadeInUp} initial="hidden" animate="visible" />
 */

// ─── Spring presets (matches Vengeance UI spring physics) ────────────────────
export const SPRING = { type: 'spring', stiffness: 300, damping: 24 };
export const SPRING_GENTLE = { type: 'spring', stiffness: 200, damping: 30 };
export const SPRING_BOUNCY = { type: 'spring', stiffness: 400, damping: 20 };
export const TWEEN = { duration: 0.25, ease: [0.16, 1, 0.3, 1] }; // expo out

// ─── Fade + Slide variants ───────────────────────────────────────────────────
export const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

export const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: TWEEN },
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: TWEEN },
};

// ─── Scale variants ────────────────────────────────────────────────────────────
export const scaleIn = {
  hidden: { opacity: 0, scale: 0.9, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: SPRING },
};

export const scaleInFast = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
};

// ─── Modal variants (backdrop + content) ──────────────────────────────────────
export const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
};

export const modalVariants = {
  hidden: { opacity: 0, scale: 0.88, y: 16, filter: 'blur(4px)' },
  visible: {
    opacity: 1, scale: 1, y: 0, filter: 'blur(0px)',
    transition: { type: 'spring', stiffness: 320, damping: 26, delay: 0.05 },
  },
  exit: {
    opacity: 0, scale: 0.92, y: 8,
    transition: { duration: 0.18, ease: [0.4, 0, 1, 1] },
  },
};

// ─── Stagger container ─────────────────────────────────────────────────────────
export const staggerContainer = (staggerChildren = 0.06, delayChildren = 0) => ({
  hidden: {},
  visible: {
    transition: { staggerChildren, delayChildren },
  },
});

// ─── Card hover ────────────────────────────────────────────────────────────────
export const cardHover = {
  rest: { y: 0, scale: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.2)', borderColor: 'rgba(255,255,255,0.06)' },
  hover: {
    y: -4, scale: 1.012,
    boxShadow: '0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(59,130,246,0.18)',
    borderColor: 'rgba(59,130,246,0.3)',
    transition: SPRING_GENTLE,
  },
};

// ─── Button variants ─────────────────────────────────────────────────────────
export const buttonTap = {
  rest: { scale: 1 },
  tap: { scale: 0.955, transition: { type: 'spring', stiffness: 500, damping: 30 } },
};

export const buttonHover = {
  rest: { scale: 1, y: 0 },
  hover: {
    scale: 1.03, y: -2,
    transition: { type: 'spring', stiffness: 400, damping: 20 },
  },
  tap: { scale: 0.96 },
};

// Glow button specific (blue)
export const btnGlowBlueHover = {
  rest: {
    background: 'rgba(59,130,246,0.12)',
    borderColor: 'rgba(59,130,246,0.3)',
    boxShadow: '0 0 16px rgba(59,130,246,0.15), 0 4px 12px rgba(0,0,0,0.3)',
  },
  hover: {
    background: 'rgba(59,130,246,0.22)',
    borderColor: 'rgba(59,130,246,0.55)',
    boxShadow: '0 0 28px rgba(59,130,246,0.32), 0 4px 16px rgba(0,0,0,0.4)',
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.97,
    boxShadow: '0 0 10px rgba(59,130,blue,0.2), 0 2px 8px rgba(0,0,0,0.3)',
  },
};

// ─── Badge / status pulse ─────────────────────────────────────────────────────
export const badgePop = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: {
    opacity: 1, scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 18, delay: 0.1 },
  },
};

// ─── List item stagger (for tables, card grids) ──────────────────────────────
export const listItem = (index) => ({
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.3, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] },
  },
});

// ─── Floating / ambient ───────────────────────────────────────────────────────
export const float = {
  animate: {
    y: [-6, 6, -6],
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
  },
};

export const floatSlow = {
  animate: {
    y: [-3, 3, -3],
    transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
  },
};

// ─── Page section reveal ──────────────────────────────────────────────────────
export const pageSection = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  },
};

// ─── Enhanced page wrap (staggered entrance) ──────────────────────────────────
export const pageWrapVariants = {
  initial: { opacity: 0, y: 18 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1],
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.15, ease: [0.4, 0, 1, 1] },
  },
};

export const staggerItem = {
  initial: { opacity: 0, y: 14 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
  },
};

